import { Heading, Input, InputStepper, Select, Shadow, Text, VStack } from '@genial/components';
import { Utils, useBreakpointValue } from '@genial/utils';
import { Box, useTheme } from 'native-base';
import React, { useCallback, useState } from 'react';
import { LayoutChangeEvent } from 'react-native';

import { LateralBar } from '../../../../../../../../../../../../components/LateralBar';
import { PrivatePensionEntityNormalized } from '../../../../../../../../../../../../store/product/types';
import { PrivatePensionAssetController } from '../../../../../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/PrivatePension';
import { PrivatePensionTaxSystem } from '../../../../../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/PrivatePension/types';

export namespace ProductInfoCardTypes {
  export interface Props {
    offer: PrivatePensionEntityNormalized;
    offerController: PrivatePensionAssetController;
  }
}

export const ProductInfoCard: React.FC<ProductInfoCardTypes.Props> = ({
  offer,
  offerController,
}) => {
  const { taxSystemOptions, accountHolderForm, setTaxSystem, setPensionAge } = offerController;
  const [maxFlexBasis, setMaxFlexBasis] = useState('');
  const theme = useTheme();
  const gap = Utils.Token.ToInt(theme.sizes, 'stack.xxxs');
  const flexBasisDivider = useBreakpointValue({ base: 1, sm: 2, lg: 4 });

  const {
    formState: { errors },
    getValues,
    control,
  } = accountHolderForm;

  const { taxSystem: formTaxSystem, pensionAge: formPensionAge } = getValues();

  const handleTaxSystemChange = useCallback(
    (newValue: string) => {
      setTaxSystem(Number(newValue) as PrivatePensionTaxSystem);
    },
    [setTaxSystem]
  );

  const handlePensionAgeChange = useCallback(
    (newValue: number) => {
      setPensionAge(Number.isNaN(newValue) ? 0 : newValue);
    },
    [setPensionAge]
  );

  const handleLayout = (e: LayoutChangeEvent) => {
    setMaxFlexBasis(`${e.nativeEvent.layout.width / flexBasisDivider - gap}px`);
  };

  return (
    <Shadow level={1} borderRadius="sm">
      <LateralBar barColor="product.previdencia" />
      <VStack padding="inset.sm">
        <Heading
          size="h3"
          testId="private-pension-account-holder-product-name"
          borderBottomColor="neutral.lightest"
          borderBottomWidth={1}
          marginBottom="inset.sm"
          paddingBottom="inset.sm">
          {offer.productNameNormalized}
        </Heading>
        <Box
          flexDirection="row"
          flexWrap="wrap"
          style={{ gap }}
          width="full"
          onLayout={handleLayout}>
          <VStack space="stack.nano" flex={1} flexBasis={maxFlexBasis}>
            <Text variant="semibold">Idade de aposentadoria desejada</Text>
            <InputStepper
              stepperValue={1}
              min={1}
              max={99}
              name="pensionAge"
              control={control}
              customError={errors.pensionAge?.message}
              defaultValue={formPensionAge}
              onChange={handlePensionAgeChange}
            />
          </VStack>
          <VStack space="stack.nano" flex={1} flexBasis={maxFlexBasis}>
            <Text variant="semibold">Tipo de regime tributário</Text>
            <Select
              placeholder="Selecione"
              options={taxSystemOptions}
              onValueChange={handleTaxSystemChange}
              maxOptionPerRender={5}
              selectedValue={formTaxSystem.toString()}
              error={errors.taxSystem}
            />
          </VStack>
          <VStack space="stack.nano" flex={1} flexBasis={maxFlexBasis}>
            <Text variant="semibold">Aporte único</Text>
            <Input
              placeholder="Digite o valor"
              variant="filled"
              name="singleContribution"
              autoComplete="off"
              hasShadow={false}
              error={errors.singleContribution}
              control={control}
              mask={Utils.Mask.InputMoney}
            />
            <Text fontSize="xs" color="brand.tertiary.principal">
              Mínimo {offer.minimumApplicationNormalized}
            </Text>
          </VStack>
          <VStack space="stack.nano" flex={1} flexBasis={maxFlexBasis}>
            <Text variant="semibold">Contribuição Mensal</Text>
            <Input
              placeholder="Digite o valor"
              variant="filled"
              name="monthlyContribution"
              autoComplete="off"
              hasShadow={false}
              error={errors.monthlyContribution}
              control={control}
              mask={Utils.Mask.InputMoney}
            />
            <Text fontSize="xs" color="brand.tertiary.principal">
              Movimentação mínima mensal {offer.monthlyMinimumApplicationNormalized}
            </Text>
          </VStack>
        </Box>
      </VStack>
    </Shadow>
  );
};
